import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import BackButton from '../../components/BackButton';
import { toast } from 'sonner';
import { Bell, Play, ExternalLink, RefreshCw, ChevronRight, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const money = (v) => (v ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const fmtDate = (d) => d ? d.split('-').reverse().join('/') : '-';

const badgeForDays = (dias, estado) => {
  if (estado === 'vencida' || dias < 0) return { text: `Vencida hace ${Math.abs(dias)}d`, cls: 'bg-red-100 text-red-800' };
  if (dias === 0) return { text: 'Vence hoy', cls: 'bg-amber-200 text-amber-900' };
  if (dias <= 3) return { text: `Vence en ${dias}d`, cls: 'bg-amber-100 text-amber-800' };
  if (dias <= 7) return { text: `${dias}d`, cls: 'bg-slate-100 text-slate-700' };
  return { text: `${dias}d`, cls: 'bg-slate-50 text-slate-500' };
};

const PaymentReview = () => {
  const [items, setItems] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const navigate = useNavigate();

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [r1, r2] = await Promise.all([
        axios.get(`${API_URL}/billing/payment-reminders/pending-review`),
        axios.get(`${API_URL}/billing/payment-reminders/logs?limit=50`),
      ]);
      setItems(r1.data);
      setLogs(r2.data);
    } catch (e) {
      toast.error('Error al cargar la revisión de cobros');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const runReview = async (dry) => {
    setRunning(true);
    try {
      const r = await axios.post(`${API_URL}/billing/payment-reminders/run?dry_run=${dry}`);
      const d = r.data;
      toast.success(dry
        ? `Simulación: revisadas ${d.reviewed}, se enviarían ${d.sent}`
        : `Revisadas ${d.reviewed}, enviados ${d.sent}, omitidas ${d.skipped}, marcadas vencidas ${d.marked_vencidas}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setRunning(false);
    }
  };

  const sendManual = async (id) => {
    try {
      await axios.post(`${API_URL}/billing/sales-documents/${id}/send-reminder`);
      toast.success('Recordatorio enviado (en cola)');
      setTimeout(fetchAll, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const vencidas = items.filter(i => i.dias_vencimiento < 0);
  const proximas = items.filter(i => i.dias_vencimiento >= 0);

  const totalVencido = vencidas.reduce((s, i) => s + (i.pendiente || 0), 0);
  const totalProximo = proximas.reduce((s, i) => s + (i.pendiente || 0), 0);

  return (
    <Layout>
      <div className="space-y-6" data-testid="payment-review-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <BackButton to="/billing" label="Volver a Intranet" />
            <h1 className="text-3xl font-manrope font-bold text-slate-900">Revisión de cobros</h1>
            <p className="text-slate-500 mt-1">Facturas vencidas y próximas a vencer (7 días). El sistema envía recordatorios automáticos cada mañana a las 09:15.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={fetchAll} variant="outline" size="sm"><RefreshCw className="w-4 h-4 mr-1" /> Actualizar</Button>
            <Button onClick={() => runReview(true)} disabled={running} variant="outline" data-testid="dry-run-btn">
              Simular envío
            </Button>
            <Button onClick={() => runReview(false)} disabled={running} data-testid="run-reminders-btn" className="bg-[#004E89] hover:bg-[#003a68]">
              <Play className="w-4 h-4 mr-1" /> Ejecutar ahora
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-white border border-red-200 rounded-lg p-4">
            <div className="text-xs text-slate-500 uppercase">Facturas vencidas</div>
            <div className="text-2xl font-bold text-red-700">{vencidas.length}</div>
            <div className="text-sm text-red-600">{money(totalVencido)}</div>
          </div>
          <div className="bg-white border border-amber-200 rounded-lg p-4">
            <div className="text-xs text-slate-500 uppercase">Próximas a vencer (7d)</div>
            <div className="text-2xl font-bold text-amber-700">{proximas.length}</div>
            <div className="text-sm text-amber-700">{money(totalProximo)}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="text-xs text-slate-500 uppercase">Recordatorios (últimos 50)</div>
            <div className="text-2xl font-bold text-slate-700">{logs.length}</div>
            <div className="text-sm text-slate-500">{logs.filter(l=>l.status==='sent').length} enviados · {logs.filter(l=>l.status==='error').length} errores</div>
          </div>
        </div>

        {/* Lista principal */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          {loading ? <div className="p-12 text-center text-slate-500">Cargando...</div>
            : items.length === 0 ? <div className="p-12 text-center text-slate-500">🎉 No hay facturas pendientes ni próximas a vencer.</div>
            : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Nº</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Cliente</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Vencimiento</th>
                      <th className="text-right p-3 text-xs font-semibold text-slate-700 uppercase">Pendiente</th>
                      <th className="text-left p-3 text-xs font-semibold text-slate-700 uppercase">Recordatorios</th>
                      <th className="text-right p-3 text-xs font-semibold text-slate-700 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(i => {
                      const badge = badgeForDays(i.dias_vencimiento, i.estado);
                      return (
                        <tr key={i.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-3 text-sm font-medium font-mono">{i.numero}</td>
                          <td className="p-3 text-sm">
                            {i.cliente}
                            <div className="text-xs text-slate-500">{i.email || <span className="text-red-500">sin email</span>}</div>
                          </td>
                          <td className="p-3 text-sm">
                            {fmtDate(i.fecha_vencimiento)}
                            <div><span className={`inline-block text-xs px-2 py-0.5 rounded-full mt-0.5 ${badge.cls}`}>{badge.text}</span></div>
                          </td>
                          <td className="p-3 text-sm text-right">
                            <div className="font-bold text-slate-900">{money(i.pendiente)}</div>
                            <div className="text-xs text-slate-500">de {money(i.total)}</div>
                          </td>
                          <td className="p-3 text-xs text-slate-600">
                            {(i.reminders_sent || []).length === 0 ? <span className="text-slate-400">Ninguno</span>
                              : (i.reminders_sent || []).map(s => (
                                <span key={s} className="inline-block bg-slate-100 rounded px-1.5 py-0.5 mr-1 mb-1 text-slate-600">{s}</span>
                              ))
                            }
                          </td>
                          <td className="p-3 text-right space-x-1">
                            <Button size="sm" variant="outline" onClick={() => navigate(`/billing/invoices/${i.id}`)} title="Abrir factura">
                              <ExternalLink className="w-3 h-3" />
                            </Button>
                            <Button size="sm" onClick={() => sendManual(i.id)} disabled={!i.email} className="bg-[#004E89] hover:bg-[#003a68]">
                              <Send className="w-3 h-3 mr-1" /> Enviar
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
        </div>

        {/* Historial */}
        {logs.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
              <Bell className="w-4 h-4 text-slate-500" />
              <h3 className="font-manrope font-semibold text-slate-900">Historial reciente</h3>
            </div>
            <div className="overflow-x-auto max-h-72">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 text-xs uppercase text-slate-600">Fecha</th>
                    <th className="text-left p-2 text-xs uppercase text-slate-600">Factura</th>
                    <th className="text-left p-2 text-xs uppercase text-slate-600">Etapa</th>
                    <th className="text-left p-2 text-xs uppercase text-slate-600">Destinatario</th>
                    <th className="text-left p-2 text-xs uppercase text-slate-600">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l, i) => (
                    <tr key={i} className="border-b">
                      <td className="p-2 text-xs text-slate-600">{new Date(l.sent_at).toLocaleString('es-ES')}</td>
                      <td className="p-2 font-mono text-xs">{l.numero}</td>
                      <td className="p-2 text-xs">{l.stage}</td>
                      <td className="p-2 text-xs">{l.to}</td>
                      <td className="p-2 text-xs">
                        <span className={`px-2 py-0.5 rounded-full ${l.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{l.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default PaymentReview;
